import { Link } from '@tanstack/react-router';
import { ChevronRight, Video } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { formatRelativeDate } from './format';
import { visioFeedView } from './visio-view';
import type { VisioFeedRow } from './types';

const TIME = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Carte Feed d'une visio / réunion TERMINÉE. Quand la réservation appartient
 * au viewer, toute la carte ouvre la page du rendez-vous passé (dialogue,
 * fichiers, résumé) ; sinon simple rappel.
 */
export function VisioCard({ data, viewerId }: { data: VisioFeedRow; viewerId?: string | null }) {
  const view = visioFeedView(data, viewerId);
  const endIso = data.ended_at ?? data.ends_at;

  const body = (
    <div className="flex items-center gap-3 px-4 pb-4">
      <div className="border-primary-300 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2">
        <Video className="text-primary-500 h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <span className="text-ink-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
          {view.kindLabel} · terminé
        </span>
        <p className="text-ink-900 truncate font-sans text-sm font-semibold leading-snug">
          {view.title ?? `${view.kindLabel} avec ${view.name}`}
        </p>
        <p className="text-ink-500 mt-0.5 font-sans text-xs">
          {TIME.format(new Date(data.starts_at))} – {TIME.format(new Date(endIso))}
          {view.durationMin > 0 && ` · ${view.durationMin} min`}
        </p>
      </div>
      {view.reservationId && (
        <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl bg-white">
      <div className="flex items-center gap-2.5 px-4 py-3">
        <AvatarCircle name={view.name} src={view.avatarUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <span className="text-ink-900 font-sans text-xs font-semibold">{view.name}</span>
          <span className="text-ink-500 font-sans text-xs"> · visio terminée</span>
        </div>
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(endIso)}
        </span>
      </div>
      {view.reservationId ? (
        <Link
          to="/rendez-vous/$reservationId"
          params={{ reservationId: view.reservationId }}
          aria-label={`Revoir ${view.kindLabel.toLowerCase()} avec ${view.name}`}
          className="block transition-transform hover:bg-white/5 active:scale-[0.98]"
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </div>
  );
}
